import * as React from 'react';
import { inject, observer } from 'mobx-react';
import moment from 'moment-timezone';
import QRCode from 'qrcode.react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { FaAward, FaCopy, FaExternalLinkAlt, FaLink, FaQrcode, FaShareAlt, FaStar, FaUserCircle, FaWallet } from 'react-icons/fa';
import { Button, FormGroup, Modal, ModalContent, RotateLoader, SelectAdv } from '@lib/components';
import { MobxComponent } from '../../../../../mobx/components';
import { UI } from '../../../../../core/ui';
import { nativeLoyaltySettings } from './helpers';
import {
  LC,
  LoyaltyButton,
  LoyaltyCard,
  LoyaltyCardHeading,
  LoyaltyShell,
} from './shell';

interface Props {}
interface State {
  loading: boolean;
  error: string;
  from: string;
  to: string;
  serviceType: string;
  data: any;
  activityView: 'graph' | 'list';
  qrModalActive: boolean;
}

const SERVICE_TYPES = [
  { label: 'All services', value: '' },
  { label: 'Pickup', value: 'pickup' },
  { label: 'Delivery', value: 'delivery' },
  { label: 'Dine-in', value: 'dine_in' },
  { label: 'Table booking', value: 'table_booking' },
];

const EARN_TYPES = ['earn', 'issue', 'accrual', 'add'];

function defaultRange() {
  return {
    from: moment().startOf('year').toISOString(),
    to: moment().endOf('day').toISOString(),
  };
}

@inject('store')
@observer
export class RestaurantLoyaltyOverview extends MobxComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    const range = defaultRange();
    this.state = {
      loading: true,
      error: '',
      from: range.from,
      to: range.to,
      serviceType: '',
      data: null,
      activityView: 'graph',
      qrModalActive: false,
    };
  }

  componentDidMount() {
    this.load();
  }

  load = async () => {
    const { store } = this.injected;
    const restaurant = store.restaurant!;
    this.setState({ loading: true, error: '' });
    try {
      const response = await store.api.native_loyalty_overview({
        restaurant_id: restaurant._id,
        from: this.state.from,
        to: this.state.to,
        ...(this.state.serviceType ? { service_type: this.state.serviceType } : {}),
      });
      if (response.outcome) {
        this.setState({ loading: false, error: response.message || 'Failed to load loyalty dashboard' });
        return;
      }
      this.setState({ loading: false, data: response.data });
    } catch (e) {
      this.setState({ loading: false, error: 'Failed to load loyalty dashboard' });
    }
  };

  /** Portal join URL (`{portal}/s/{locationId}`) — built by the API from its own config so it
   * tracks the deployed portal origin; see `nativeLoyaltyProgramLink` in
   * `src/api/src/routes/v1/native-loyalty-dashboard.ts`. */
  programLink(): string {
    const native = nativeLoyaltySettings(this.injected.store.restaurant);
    if (!native?.merchant_id || !native?.location_id) return '';
    return this.state.data?.programLink || '';
  }

  copyLink = async () => {
    const link = this.programLink();
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      UI.notification.success('Loyalty program link copied');
    } catch (e) {
      UI.notification.error('Unable to copy the link');
    }
  };

  shareLink = async () => {
    const link = this.programLink();
    if (!link) return;
    const nav = navigator as any;
    if (nav.share) {
      try {
        await nav.share({ title: 'Loyalty program', url: link });
        return;
      } catch (e) {
        return;
      }
    }
    this.copyLink();
  };

  /** Calendar year used for the activity chart (matches the overview `to` date). */
  chartYear() {
    return moment(this.state.to).year();
  }

  /** Groups activity into Jan–Dec earned / redeemed totals for `chartYear()` (zeros when no rows). */
  chartData(activity: any[]) {
    const year = this.chartYear();
    const months: { [key: string]: { label: string; sort: string; earned: number; redeemed: number } } = {};
    for (let month = 0; month < 12; month++) {
      const date = moment({ year, month, day: 1 });
      const key = date.format('YYYY-MM');
      months[key] = { label: date.format('MMM'), sort: key, earned: 0, redeemed: 0 };
    }
    activity.forEach(row => {
      const date = moment(row.createdAt);
      if (!date.isValid() || date.year() !== year) return;
      const key = date.format('YYYY-MM');
      if (!months[key]) return;
      const type = String(row.type || '').toLowerCase();
      const value = Math.abs(Number(row.value) || 0);
      if (EARN_TYPES.some(earn => type.indexOf(earn) !== -1)) {
        months[key].earned += value;
      } else {
        months[key].redeemed += value;
      }
    });
    return Object.keys(months)
      .sort()
      .map(key => months[key]);
  }

  metrics(data: any) {
    return [
      { label: 'Total Members', value: data.members ?? 0, icon: <FaUserCircle size={16} color={LC.label} /> },
      { label: 'Points Issued', value: (data.pointsIssued ?? 0).toLocaleString(), icon: <FaStar size={16} color={LC.label} /> },
      { label: 'Points Redeemed', value: (data.pointsRedeemed ?? 0).toLocaleString(), icon: <FaAward size={16} color={LC.label} /> },
      { label: 'Gift Card Liability', value: (data.giftCardLiability ?? 0).toLocaleString(), icon: <FaWallet size={16} color={LC.label} /> },
    ];
  }

  renderMetrics(data: any) {
    return (
      <div className="flex-line flex-wrap" style={{ gap: 16, alignItems: 'stretch' }}>
        {this.metrics(data).map(metric => (
          <div
            key={metric.label}
            className="flex-line"
            style={{
              flex: '1 0 180px',
              gap: 12,
              alignItems: 'center',
              background: LC.white,
              border: `1px solid ${LC.metricBorder}`,
              borderRadius: 12,
              padding: 20,
              boxShadow: '0px 1px 1px rgba(10,13,18,0.05)',
            }}
          >
            {/* Icon tile */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: LC.tile,
                borderRadius: 8,
                padding: 8,
                flexShrink: 0,
                width: 32,
                height: 32,
              }}
            >
              {metric.icon}
            </div>
            {/* Label + value */}
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 12, lineHeight: '18px', color: LC.tertiary, fontWeight: 400 }}>
                {metric.label}
              </p>
              <p style={{ margin: 0, fontSize: 18, lineHeight: '26px', fontWeight: 600, color: LC.strong }}>
                {metric.value}
              </p>
            </div>
          </div>
        ))}
      </div>
    );
  }

  renderProgramLink() {
    const link = this.programLink();

    const linkBtn = (
      onClick: () => void,
      icon: React.ReactNode,
      label: string,
      disabled = false
    ) => (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          height: 32,
          padding: '0 12px',
          background: LC.white,
          border: '1px solid #d5d7da',
          borderRadius: 8,
          boxShadow: '0px 1px 2px rgba(10,13,18,0.05)',
          color: '#414651',
          fontSize: 13,
          fontWeight: 600,
          whiteSpace: 'nowrap' as const,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          flexShrink: 0,
        }}
      >
        {icon}
        {label}
      </button>
    );

    return (
      <div
        className="flex-line flex-wrap"
        style={{
          gap: 12,
          alignItems: 'center',
          background: LC.white,
          border: `1px solid ${LC.metricBorder}`,
          borderRadius: 12,
          padding: 12,
          boxShadow: '0px 1px 1px rgba(10,13,18,0.05)',
        }}
      >
        {/* Icon tile */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: LC.tile,
            borderRadius: 8,
            padding: 8,
            flexShrink: 0,
            width: 32,
            height: 32,
          }}
        >
          <FaAward size={16} color={LC.label} />
        </div>

        {/* Text block */}
        <div style={{ flex: '1 0 220px', minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13, lineHeight: '20px', fontWeight: 600, color: LC.strong }}>
            Loyalty Program Link
          </p>
          <p style={{ margin: '2px 0 0', fontSize: 12, lineHeight: '18px', color: '#1a1a54', wordBreak: 'break-all' }}>
            {link || 'Connect Quaddo in Integrations to generate a link.'}
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex-line flex-wrap" style={{ gap: 8, flexShrink: 0 }}>
          {linkBtn(this.copyLink, <FaCopy size={14} />, 'Copy Link', !link)}
          {linkBtn(
            () => this.setState({ qrModalActive: true }),
            <FaQrcode size={14} />,
            'Download QR',
            !link
          )}
          {linkBtn(
            () => link && window.open(link, '_blank'),
            <FaExternalLinkAlt size={13} />,
            'View Landing Page',
            !link
          )}
        </div>
      </div>
    );
  }

  renderActivity(activity: any[]) {
    const { activityView } = this.state;
    const chartData = this.chartData(activity);

    const EARNED_COLOR = '#ffbbbb';
    const REDEEMED_COLOR = '#fb2f2f';

    const tabBtn = (label: string, view: 'graph' | 'list') => {
      const active = activityView === view;
      return (
        <button
          key={view}
          type="button"
          onClick={() => this.setState({ activityView: view })}
          style={{
            padding: '4px 12px',
            background: 'transparent',
            border: 'none',
            borderBottom: active ? `2px solid ${REDEEMED_COLOR}` : '2px solid transparent',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 600,
            color: active ? REDEEMED_COLOR : LC.muted,
            lineHeight: '24px',
          }}
        >
          {label}
        </button>
      );
    };

    return (
      <div
        style={{
          background: LC.white,
          border: `1px solid ${LC.metricBorder}`,
          borderRadius: 8,
          padding: 16,
        }}
      >
        {/* Header row */}
        <div
          className="flex-line"
          style={{ alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}
        >
          <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: LC.strong }}>Points Activity</p>
          <div style={{ display: 'flex' }}>
            {tabBtn('Graph', 'graph')}
            {tabBtn('List', 'list')}
          </div>
        </div>

        {/* Empty state for list */}
        {activity.length === 0 && activityView === 'list' && (
          <p style={{ color: LC.muted, margin: 0 }}>No activity in this range.</p>
        )}

        {/* Graph */}
        {activityView === 'graph' && (
          <>
            <div style={{ width: '100%', height: 380 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eceef0" />
                  <XAxis dataKey="label" tick={{ fill: LC.muted, fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fill: LC.muted, fontSize: 11 }} tickLine={false} axisLine={false} width={52} label={{ value: 'Points', angle: -90, position: 'insideLeft', fill: LC.muted, fontSize: 11 }} />
                  <Tooltip cursor={{ fill: 'rgba(26,26,84,0.04)' }} />
                  <Bar dataKey="earned" name="Points Earned" stackId="points" fill={EARNED_COLOR} />
                  <Bar dataKey="redeemed" name="Points Redeemed" stackId="points" fill={REDEEMED_COLOR} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {/* Legend */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              {[
                { color: EARNED_COLOR, label: 'Points Earned' },
                { color: REDEEMED_COLOR, label: 'Points Redeemed' },
              ].map(legend => (
                <div key={legend.label} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px' }}>
                  <div style={{ width: 16, height: 16, borderRadius: 3, background: legend.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: LC.tertiary }}>{legend.label}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* List */}
        {activityView === 'list' && activity.length > 0 && (
          <div>
            {activity.map((row: any) => {
              const type = String(row.type || '').toLowerCase();
              const isGiftCard = type.includes('gift_card') || type.includes('giftcard') || type.includes('gift card');
              const isEarn = EARN_TYPES.some(t => type.includes(t));
              const rawValue = Number(row.value) || 0;

              let displayValue: string;
              if (isGiftCard) {
                const cc = this.injected.store.restaurant?.settings?.region?.currency?.code ?? 'USD';
                const precision = this.injected.store.restaurant?.settings?.region?.currency?.precision ?? 2;
                try {
                  displayValue = new Intl.NumberFormat(undefined, {
                    style: 'currency',
                    currency: cc,
                    minimumFractionDigits: precision,
                    maximumFractionDigits: precision,
                  }).format(Math.abs(rawValue) / Math.pow(10, precision));
                } catch {
                  displayValue = `$${(Math.abs(rawValue) / 100).toFixed(2)}`;
                }
              } else {
                displayValue = `+${Math.abs(rawValue).toLocaleString()}`;
              }

              const valueColor = isGiftCard ? '#009100' : isEarn ? LC.title : '#fb2f2f';

              return (
                <div
                  key={row.id}
                  className="flex-l-r-center"
                  style={{ borderBottom: `1px solid ${LC.rowBorder}`, padding: '10px 0' }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <strong style={{ fontSize: 13, color: LC.title, textTransform: 'capitalize' }}>{row.type}</strong>
                    <span style={{ color: LC.muted, fontSize: 11 }}>{row.createdAt}</span>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: valueColor }}>{displayValue}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  renderQRModal() {
    const link = this.programLink();
    return (
      <Modal
        id="loyalty-qr-modal"
        width={520}
        active={this.state.qrModalActive}
        alignTop={true}
        close={() => this.setState({ qrModalActive: false })}
      >
        <ModalContent paddingtb={24} paddinglr={24}>
          <div style={{ fontSize: 24, lineHeight: '32px', fontWeight: 700, color: LC.title, marginBottom: 24 }}>
            Loyalty QR Code
          </div>
          <div className="flex-center" style={{ display: 'flex', marginBottom: 24 }}>
            <div style={{ position: 'relative', padding: 8 }}>
              <QRCode value={link || ' '} size={294} />
              {[
                { top: 0, left: 0, borderTop: true, borderLeft: true },
                { top: 0, right: 0, borderTop: true, borderRight: true },
                { bottom: 0, left: 0, borderBottom: true, borderLeft: true },
                { bottom: 0, right: 0, borderBottom: true, borderRight: true },
              ].map((corner, index) => (
                <div
                  key={index}
                  style={{
                    position: 'absolute',
                    width: 12,
                    height: 12,
                    top: corner.top,
                    left: corner.left,
                    right: corner.right,
                    bottom: corner.bottom,
                    borderTop: corner.borderTop ? `2px solid ${LC.red}` : undefined,
                    borderBottom: corner.borderBottom ? `2px solid ${LC.red}` : undefined,
                    borderLeft: corner.borderLeft ? `2px solid ${LC.red}` : undefined,
                    borderRight: corner.borderRight ? `2px solid ${LC.red}` : undefined,
                    borderRadius: 2,
                  }}
                />
              ))}
            </div>
          </div>
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 14, lineHeight: '24px', fontWeight: 600, color: LC.label, marginBottom: 4 }}>
              Loyalty Program Link
            </div>
            <input
              readOnly
              value={link}
              style={{
                width: '100%',
                background: '#e9ecef',
                border: `1px solid ${LC.border}`,
                borderRadius: 4,
                padding: 12,
                fontSize: 16,
                lineHeight: '24px',
                color: LC.muted,
              }}
            />
          </div>
          <div className="flex-line" style={{ gap: 10 }}>
            <LoyaltyButton onClick={this.shareLink} style={{ flex: '1 0 0' }}>
              <FaShareAlt size={20} color={LC.tertiary} />
              Share
            </LoyaltyButton>
            <LoyaltyButton onClick={this.copyLink} style={{ flex: '1 0 0' }}>
              <FaLink size={20} color={LC.tertiary} />
              Copy
            </LoyaltyButton>
          </div>
        </ModalContent>
      </Modal>
    );
  }

  render() {
    const { loading, error, data, from, to, serviceType } = this.state;
    const activity = data?.recentActivity || [];

    return (
      <LoyaltyShell>
        {error && <FormGroup error={error} />}
        {loading && (
          <div className="m-t-6">
            <RotateLoader />
          </div>
        )}
        {!loading && !error && !data && <p>No loyalty activity for this range.</p>}
        {!loading && data && (
          <>
            {this.renderMetrics(data)}
            {this.renderProgramLink()}
            {this.renderActivity(activity)}
          </>
        )}
        {this.renderQRModal()}
      </LoyaltyShell>
    );
  }
}
